import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import {
  Webhook, Plus, Copy, RefreshCw, Trash2, CheckCircle, AlertCircle, Clock,
  Send, ChevronDown, ChevronRight, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_TONE = {
  delivered: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  pending:   'bg-amber-100 text-amber-800 border-amber-200',
  failed:    'bg-rose-100 text-rose-800 border-rose-200',
  skipped:   'bg-slate-100 text-slate-600 border-slate-200',
};

export default function WebhooksSettings() {
  const [endpoints, setEndpoints] = useState([]);
  const [catalog, setCatalog] = useState({ events: [], signature_header: 'X-Out-Play-Signature', signature_scheme: '' });
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createdSecret, setCreatedSecret] = useState(null);   // reveal-once payload
  const [expandedId, setExpandedId] = useState(null);
  const [deliveriesById, setDeliveriesById] = useState({});

  const fetchAll = useCallback(async () => {
    try {
      const [e, c] = await Promise.all([
        api.get('/academy/webhooks'),
        api.get('/academy/webhooks/events'),
      ]);
      setEndpoints(e.data?.webhooks || []);
      setCatalog(c.data || { events: [] });
    } catch {
      toast.error('Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const loadDeliveries = async (id) => {
    if (deliveriesById[id]) return;
    try {
      const { data } = await api.get(`/academy/webhooks/${id}/deliveries`, { params: { limit: 20 } });
      setDeliveriesById((p) => ({ ...p, [id]: data?.deliveries || [] }));
    } catch {
      toast.error('Failed to load deliveries');
    }
  };

  const handleToggle = async (wh) => {
    try {
      await api.put(`/academy/webhooks/${wh.id}`, { is_active: !wh.is_active });
      fetchAll();
    } catch { toast.error('Update failed'); }
  };

  const handleDelete = async (wh) => {
    if (!window.confirm(`Delete webhook "${wh.name}"? Any pending retries will be skipped.`)) return;
    try {
      await api.delete(`/academy/webhooks/${wh.id}`);
      toast.success('Webhook deleted');
      fetchAll();
    } catch { toast.error('Delete failed'); }
  };

  const handleTest = async (wh) => {
    try {
      const { data } = await api.post(`/academy/webhooks/${wh.id}/test`);
      if (data.status === 'delivered') {
        toast.success(`Ping delivered · HTTP ${data.status_code}`);
      } else {
        toast.error(`Ping ${data.status} · ${data.error || `HTTP ${data.status_code}`}`);
      }
      // Refresh deliveries log
      setDeliveriesById((p) => ({ ...p, [wh.id]: undefined }));
      if (expandedId === wh.id) loadDeliveries(wh.id);
    } catch { toast.error('Test ping failed'); }
  };

  const handleRotate = async (wh) => {
    if (!window.confirm(`Rotate the signing secret for "${wh.name}"? Receivers using the old secret will reject deliveries until you update them.`)) return;
    try {
      const { data } = await api.post(`/academy/webhooks/${wh.id}/rotate-secret`);
      setCreatedSecret({ id: wh.id, secret: data.secret, url: wh.url, name: wh.name, rotated: true });
      toast.success('Secret rotated');
      fetchAll();
    } catch { toast.error('Rotate failed'); }
  };

  return (
    <div className="space-y-4" data-testid="webhooks-settings-pane">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Webhook className="h-4 w-4 text-teal-700" /> Outbound Webhooks
              </CardTitle>
              <CardDescription className="text-xs mt-1">
                Sync Out-Play events to your CRM / data warehouse / Zap. Every delivery is HMAC-signed with header <code className="text-[10px] bg-muted px-1 py-0.5 rounded">{catalog.signature_header}</code>.
              </CardDescription>
            </div>
            <Button size="sm" onClick={() => setCreateOpen(true)} className="h-8 text-xs"
                    data-testid="webhook-create-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add webhook
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
          ) : endpoints.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-border rounded-lg">
              <Webhook className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No webhook endpoints yet.</p>
              <p className="text-[11px] text-muted-foreground mt-1">Add one to start receiving real-time events.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {endpoints.map((wh) => (
                <div key={wh.id} className="border border-border rounded-lg p-3 hover:bg-muted/30 transition-colors"
                     data-testid={`webhook-row-${wh.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button type="button"
                                className="text-left flex items-center gap-1 group"
                                onClick={() => {
                                  const next = expandedId === wh.id ? null : wh.id;
                                  setExpandedId(next);
                                  if (next) loadDeliveries(wh.id);
                                }}
                                data-testid={`webhook-expand-${wh.id}`}>
                          {expandedId === wh.id ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                          <span className="text-sm font-semibold">{wh.name}</span>
                        </button>
                        {wh.is_active
                          ? <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge>
                          : <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600">Paused</Badge>}
                        {wh.failure_count > 0 && (
                          <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200">
                            {wh.failure_count} failure{wh.failure_count !== 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1 font-mono truncate" title={wh.url}>{wh.url}</p>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {(wh.events || []).map((e) => (
                          <span key={e} className="px-1.5 py-0.5 text-[10px] rounded bg-teal-50 text-teal-800 border border-teal-200">{e}</span>
                        ))}
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1.5">
                        Secret: <code className="font-mono">{wh.secret_preview || '—'}</code>
                        {' · '}
                        {wh.delivery_count > 0
                          ? `${wh.delivery_count} delivered · last ${wh.last_delivered_at?.slice(0, 16) || '—'}`
                          : 'Never fired'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Switch checked={wh.is_active} onCheckedChange={() => handleToggle(wh)}
                              data-testid={`webhook-toggle-${wh.id}`} />
                      <Button size="sm" variant="outline" className="h-7 text-[11px]"
                              onClick={() => handleTest(wh)} data-testid={`webhook-test-${wh.id}`}>
                        <Send className="h-3 w-3 mr-1" /> Test
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-[11px]"
                              onClick={() => handleRotate(wh)} data-testid={`webhook-rotate-${wh.id}`}>
                        <RefreshCw className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-[11px] text-rose-700 hover:bg-rose-50"
                              onClick={() => handleDelete(wh)} data-testid={`webhook-delete-${wh.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Expanded deliveries log */}
                  {expandedId === wh.id && (
                    <div className="mt-3 pt-3 border-t border-border" data-testid={`webhook-deliveries-${wh.id}`}>
                      <p className="text-[10px] uppercase font-semibold text-muted-foreground mb-2">Recent deliveries</p>
                      {!deliveriesById[wh.id] ? (
                        <p className="text-[11px] text-muted-foreground"><Loader2 className="h-3 w-3 inline animate-spin mr-1" /> Loading…</p>
                      ) : deliveriesById[wh.id].length === 0 ? (
                        <p className="text-[11px] text-muted-foreground italic">No deliveries yet — hit "Test" to fire a ping.</p>
                      ) : (
                        <div className="space-y-1.5">
                          {deliveriesById[wh.id].map((d) => (
                            <div key={d.id} className="flex items-center gap-2 text-[11px]"
                                 data-testid={`webhook-delivery-${d.id}`}>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${STATUS_TONE[d.status] || STATUS_TONE.skipped}`}>
                                {d.status}
                              </span>
                              <span className="font-mono text-muted-foreground">{d.event}</span>
                              <span className="text-muted-foreground">·</span>
                              <span className="text-muted-foreground tabular-nums">attempt {d.attempts}</span>
                              {d.last_status_code && (
                                <span className="text-muted-foreground tabular-nums">· HTTP {d.last_status_code}</span>
                              )}
                              {d.last_error && (
                                <span className="text-rose-700 truncate" title={d.last_error}>· {d.last_error.slice(0, 60)}</span>
                              )}
                              <span className="text-muted-foreground ml-auto">{(d.last_attempt_at || d.created_at || '').slice(11, 16)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <CreateWebhookDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        catalog={catalog}
        onCreated={(payload) => { setCreatedSecret(payload); fetchAll(); }}
      />

      <RevealSecretDialog
        revealed={createdSecret}
        onClose={() => setCreatedSecret(null)}
      />
    </div>
  );
}

function CreateWebhookDialog({ open, onOpenChange, catalog, onCreated }) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) { setName(''); setUrl(''); setSelectedEvents([]); }
  }, [open]);

  const toggleEvent = (key) => {
    setSelectedEvents((p) => p.includes(key) ? p.filter((x) => x !== key) : [...p, key]);
  };

  const handleSubmit = async () => {
    if (!url.startsWith('http')) { toast.error('URL must start with http(s)'); return; }
    if (selectedEvents.length === 0) { toast.error('Pick at least one event'); return; }
    try {
      setSubmitting(true);
      const { data } = await api.post('/academy/webhooks', {
        name: name || 'Untitled webhook', url, events: selectedEvents,
      });
      onCreated?.(data);
      onOpenChange(false);
      toast.success('Webhook created');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="webhook-create-dialog">
        <DialogHeader>
          <DialogTitle>Add a Webhook Endpoint</DialogTitle>
          <DialogDescription>
            We'll POST a signed JSON payload to this URL whenever any subscribed event fires.
            The signing secret is shown ONCE on creation — save it then.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="wh-name">Friendly name</Label>
            <Input id="wh-name" value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="e.g. HubSpot Production" data-testid="webhook-name-input" />
          </div>
          <div>
            <Label htmlFor="wh-url">Endpoint URL</Label>
            <Input id="wh-url" value={url} onChange={(e) => setUrl(e.target.value)}
                   placeholder="https://hooks.example.com/outplay" data-testid="webhook-url-input" />
          </div>
          <div>
            <Label>Events</Label>
            <div className="space-y-1 mt-1.5 max-h-52 overflow-y-auto border border-border rounded p-2">
              {(catalog.events || []).map((e) => (
                <label key={e.key} className="flex items-start gap-2 py-1 cursor-pointer hover:bg-muted/40 px-1 rounded"
                       data-testid={`webhook-event-${e.key}`}>
                  <input type="checkbox" checked={selectedEvents.includes(e.key)} onChange={() => toggleEvent(e.key)}
                         className="mt-1" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold">{e.label}</p>
                    <p className="text-[10px] text-muted-foreground">{e.description}</p>
                  </div>
                  <code className="text-[9px] text-muted-foreground bg-muted px-1 py-0.5 rounded shrink-0">{e.key}</code>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="webhook-create-submit">
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
            Create webhook
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevealSecretDialog({ revealed, onClose }) {
  if (!revealed) return null;
  const handleCopy = () => {
    navigator.clipboard.writeText(revealed.secret).then(() => toast.success('Secret copied'));
  };
  return (
    <Dialog open={!!revealed} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="webhook-secret-reveal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-emerald-600" /> {revealed.rotated ? 'Secret rotated' : 'Webhook created'}
          </DialogTitle>
          <DialogDescription>
            Save this signing secret <strong>now</strong> — it won't be shown again.
            Verify incoming `X-Out-Play-Signature` headers against this value using HMAC-SHA256 of <code className="text-[10px] bg-muted px-1 py-0.5 rounded">{`{ts}.{body}`}</code>.
          </DialogDescription>
        </DialogHeader>
        <div className="bg-muted rounded p-3 font-mono text-[11px] break-all border border-border" data-testid="webhook-revealed-secret">
          {revealed.secret}
        </div>
        <p className="text-[10px] text-muted-foreground mt-1">Endpoint: {revealed.url}</p>
        <DialogFooter>
          <Button variant="outline" onClick={handleCopy} data-testid="webhook-copy-secret-btn">
            <Copy className="h-3.5 w-3.5 mr-1" /> Copy secret
          </Button>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
